import React from "react";
import { Card, Row, Col, Grid, Statistic, Select, Button as AntButton } from "antd";
import { useDispatch } from "react-redux";
import { getLocalStorageData } from "../../utils/LocalStorageService";
import {
    useGetCarbonLifecycleStatsQuery,
    useSimulateRiskEventMutation
} from "../../redux-toolkit/services/dashboardService";
import { useFindAllOrganizationMasterMutation } from "../../redux-toolkit/services/organizationMasterService";
import { Button } from "react-bootstrap";
import { alertMessage } from "../../utils/AlertService";
import Loading from "../Loading";
import "./dashboard.scss";

const { useBreakpoint } = Grid;

const CarbonLifecycleDashboard: React.FC = () => {
    const screens = useBreakpoint();
    const localUserObj: any = getLocalStorageData("localUser");
    const [selectedEntity, setSelectedEntity] = React.useState<number | undefined>(undefined);

    const { data: stats, isLoading, isError } = useGetCarbonLifecycleStatsQuery({
        token: localUserObj?.token,
        entityId: selectedEntity
    });

    const [simulateRisk] = useSimulateRiskEventMutation();
    const [findAllOrgs] = useFindAllOrganizationMasterMutation();
    const [organizations, setOrganizations] = React.useState<any[]>([]);

    React.useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const response = await findAllOrgs({ token: localUserObj?.token }).unwrap();
                setOrganizations(response || []);
            } catch (error) {
                console.error("Failed to fetch organizations", error);
            }
        };
        fetchOrgs();
    }, [findAllOrgs, localUserObj?.token]);

    if (isLoading) return <Loading />;

    const summaryCards = [
        { title: "Total Projects", value: stats?.totalProjects || 0, unit: "", color: "#1e2a38" },
        { title: "Land Under Management", value: stats?.landUnderManagement || 0, unit: "ha", color: "#2980b9" },
        { title: "Credits Minted", value: stats?.creditsMinted || 0, unit: "", color: "#27ae60" },
        { title: "Available Credits", value: stats?.availableCredits || 0, unit: "", color: "#2ca02c" },
        { title: "Credits Sold", value: stats?.creditsSold || 0, unit: "", color: "#d62728" },
        { title: "Credits Retired", value: stats?.creditsRetired || 0, unit: "", color: "#9467bd" },
    ];

    const simulateRiskEvent = async () => {
        try {
            const result = await simulateRisk(localUserObj?.token).unwrap();
            alertMessage(result.message, "warning");
        } catch (error) {
            alertMessage("Failed to simulate risk event.", "error");
        }
    };

    return (
        <div className="bg-white px-4 py-3 rounded shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                    <h1 className="fw-bold mb-1" style={{ color: "#1e2a38", fontSize: "1.75rem" }}>
                        Carbon Lifecycle Dashboard
                    </h1>
                    <p className="text-muted mb-0">
                        {stats?.portfolioSummary || "Global Portfolio"}
                    </p>
                </div>
                <div className="d-flex gap-3 align-items-center">
                    {/* <Select
                        placeholder="Select Organization"
                        style={{ width: 250 }}
                        allowClear
                        onChange={(value) => setSelectedEntity(value)}
                        className="shadow-sm"
                    >
                        {organizations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select> */}
                    <Button
                        variant="danger"
                        className="fw-bold px-4 rounded-pill shadow-sm"
                        onClick={simulateRiskEvent}
                    >
                        Simulate Risk Event
                    </Button>
                </div>
            </div>

            <Row gutter={[20, 20]}>
                {summaryCards.map((card, index) => (
                    <Col key={index} xs={24} sm={12} md={8} lg={4}>
                        <Card
                            bordered={false}
                            className="h-100 shadow-sm transition-transform hover-scale"
                            style={{ borderTop: `4px solid ${card.color}`, borderRadius: "8px" }}
                        >
                            <Statistic
                                title={<span className="text-uppercase fw-bold" style={{ fontSize: "0.75rem", color: "#6c757d" }}>{card.title}</span>}
                                value={card.value}
                                suffix={<small style={{ fontSize: "0.8rem", color: "#6c757d" }}>{card.unit}</small>}
                                valueStyle={{ color: "#1e2a38", fontWeight: "700", fontSize: "1.5rem" }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            <div className="mt-5 p-4 border rounded bg-light">
                <h5 className="fw-bold mb-3" style={{ color: "#1e2a38" }}>Portfolio Insights</h5>
                <p className="text-muted">
                    {stats?.portfolioInsights || "Loading data-driven insights..."}
                </p>
            </div>
        </div>
    );
};

export default CarbonLifecycleDashboard;
